import { instantiate, ITEMS, PLAYER, CHARACTERS, ENEMIES } from '../data';

export const calculateLevelUpCost = (currentLevel) => {
  return 100 + (currentLevel * 75);
};

export const canLevelUp = (character) => {
  if (character.level >= character.maxLevel) {
    return false;
  }
  const cost = calculateLevelUpCost(character.level);
  return PLAYER.resources.premiumCurrency >= cost;
};

export const recomputeStats = (thing, prototype) => {
  const baseStats = { ...prototype.stats };

  // Apply level-based increase
  const levelMultiplier = 1 + ((thing.level || 1) - 1) * 0.05; // 5% increase per level
  for (let stat in baseStats) {
    if (stat !== 'criticalRate') {
      baseStats[stat] = Math.round(baseStats[stat] * levelMultiplier);
    }
  }

  return baseStats;
}

export const recomputeItemStats = (item) => {
  return recomputeStats(item, ITEMS[item.prototypeId]);
};

export const recomputeEnemyStats = (character) => {
  return recomputeStats(character, ENEMIES[character.prototypeId]);
};

export const recomputeCharacterStats = (character) => {
  const prototype = CHARACTERS[character.prototypeId];
  const baseStats = { ...prototype.stats };

  // Apply level-based increase
  const levelMultiplier = 1 + (character.level - 1) * 0.05; // 5% increase per level
  for (let stat in baseStats) {
    if (stat !== 'criticalRate') {
      baseStats[stat] = Math.round(baseStats[stat] * levelMultiplier);
    }
  }

  // Apply item bonuses
  character.items?.forEach(itemInstance => {
    const item = ITEMS[itemInstance.prototypeId];
    if (item && item.stats) {
      Object.entries(item.stats).forEach(([stat, value]) => {
        if (baseStats[stat] !== undefined) {
          baseStats[stat] += value;
        }
      });
    }
  });

  // Apply ascension bonuses
  if (prototype.ascensionBonuses) {
    const ascensionLevel = prototype.ascensionBonuses.findIndex(bonus => bonus.pullsRequired > character.pulls);
    const currentAscensionLevel = ascensionLevel === -1 ? prototype.ascensionBonuses.length : ascensionLevel;

    for (let i = 0; i < currentAscensionLevel; i++) {
      const bonus = prototype.ascensionBonuses[i].bonus;
      if (typeof bonus === 'object') {
        if (bonus.stat === 'all') {
          for (let stat in baseStats) {
            baseStats[stat] = Math.round(baseStats[stat] * (1 + bonus.increase));
          }
        } else {
          baseStats[bonus.stat] = Math.round(baseStats[bonus.stat] * (1 + bonus.increase));
        }
      }
    }
  }

  return baseStats;
};

export const processLevelUp = (character) => {
  const cost = calculateLevelUpCost(character.level);

  if (!canLevelUp(character)) {
    return null;
  }

  // Find character in PLAYER.characters and update directly
  const characterIndex = PLAYER.characters.findIndex(c => c.prototypeId === character.prototypeId);
  if (characterIndex !== -1) {
    const updatedCharacter = PLAYER.characters[characterIndex];
    updatedCharacter.level += 1;
    PLAYER.resources.premiumCurrency -= cost;

    // Use recomputeCharacterStats to update stats
    updatedCharacter.stats = recomputeCharacterStats(updatedCharacter);

    return true;
  }
  return false;
};

export const pullItem = (itemId) => {
  const prototype = ITEMS[itemId];

  // Add new character to player's roster
  const newItem = instantiate({
    ...prototype,
    level: 1,
    pulls: 1
  });
  newItem.stats = recomputeItemStats(newItem);
  PLAYER.items.push(newItem);
  return newItem;
};

export const pullCharacter = (characterId) => {
  const character = PLAYER.characters.find(c => c.prototypeId === characterId);
  const prototype = CHARACTERS[characterId];

  if (!character) {
    // Add new character to player's roster
    const newCharacter = {
      ...prototype,
      id: characterId,
      level: 1,
      pulls: 1
    };
    newCharacter.stats = recomputeCharacterStats(newCharacter);
    PLAYER.characters.push(newCharacter);
    return newCharacter;
  }

  // Existing character, increase pulls and recompute stats
  character.pulls += 1;
  character.stats = recomputeCharacterStats(character);

  return character;
};

export const recomputePlayerData = () => {
  PLAYER.characters?.forEach(character => {
    character.stats = recomputeCharacterStats(character);
  });
};
